import { InsuranceCarrier } from '@shared/insuranceCarrierDefs';
import { FundingPolicyType } from './appModel';

// Currently no feature flags for carriers. All carriers are enabled.
const CARRIER_FEATURE_FLAG_MAP: Partial<Record<InsuranceCarrier, FeatureFlagKeys | null>> = {
  [InsuranceCarrier.CareScout]: 'quoteApplyCareScout',
  [InsuranceCarrier.LincolnFinancial]: null,
  [InsuranceCarrier.SecurianFinancial]: null,
  [InsuranceCarrier.EquiTrust]: 'quoteApplyEquiTrust',
  [InsuranceCarrier.NewYorkLife]: 'quoteApplyNewYorkLife',
  [InsuranceCarrier.ManhattanLife]: 'quoteApplyManhattanLife',
};

/**
 * Opt-in flags at funding-policy-type granularity, parallel to
 * CARRIER_FEATURE_FLAG_MAP but one level down. A policy type listed here is
 * gated on the quote/illustration endpoints unless the advisor has the mapped
 * flag enabled (default OFF / opt-in) — it does NOT hide the type from the
 * manual add-policy form, which stays open so advisors can record a policy a
 * client already owns. Used to gate a single product on production without
 * affecting sibling products from the same carrier (e.g. gate SecureCare IV
 * while leaving SecureCare III untouched).
 */
const POLICY_TYPE_FEATURE_FLAG_MAP: Partial<Record<FundingPolicyType, FeatureFlagKeys>> = {
  [FundingPolicyType.manhattanLifeOmniFlexStc]: 'quoteApplyManhattanLife',
};

/**
 * Policy types gated on an **AppConfig** flag, managed centrally in the wizard
 * and resolved per advisor from `allAdvisors` + `advisors[advisorId]`.
 *
 * This is the mechanism to reach for on new products. The map above reads the
 * per-advisor `Advisors.featureFlags` JSON column instead, which is the older
 * arrangement and can only be changed with a database write.
 *
 * Same default and same scope as the map above: OFF unless the flag is on, and
 * quote / illustration surfaces only — the manual add-policy form stays open so
 * an advisor can still record a policy a client already owns.
 */
const APP_CONFIG_POLICY_TYPE_FEATURE_FLAG_MAP: Partial<
  Record<FundingPolicyType, AppConfigFeatureFlagKey>
> = {
  // Both annuity variants share the Nationwide carrier with CareMatters II, so
  // the gate has to sit at policy-type level — flagging the carrier in
  // CARRIER_FEATURE_FLAG_MAP would hide CareMatters II too.
  [FundingPolicyType.careMattersAnnuity]: 'quoteApplyCareMattersAnnuity',
  [FundingPolicyType.jointCareMattersAnnuity]: 'quoteApplyCareMattersAnnuity',
};

/**
 * Policy types the carrier has stopped selling. These deliberately stay listed in
 * insuranceCarrierDefs so an existing policy still resolves back to its carrier
 * (PUI history, PUI CTA, BGA lookup) — retiring a product must not break clients
 * who already own one. They are hidden only where an advisor picks products to
 * offer going forward.
 */
const RETIRED_POLICY_TYPES: ReadonlySet<FundingPolicyType> = new Set([
  FundingPolicyType.secureCareThree,
  FundingPolicyType.noLapseGuaranteeLife,
  FundingPolicyType.annuityCareTwo,
  FundingPolicyType.indexedAnnuityCare,
  FundingPolicyType.nylSecureCare,
  FundingPolicyType.nylJointSecureCare,
]);

export function isPolicyTypeRetired(policyType: FundingPolicyType): boolean {
  return RETIRED_POLICY_TYPES.has(policyType);
}

export function getCarrierFeatureFlag(carrierId: InsuranceCarrier): FeatureFlagKeys | null {
  return CARRIER_FEATURE_FLAG_MAP[carrierId] || null;
}

/**
 * Returns whether a funding policy type is available to an advisor. Policy types
 * not listed in POLICY_TYPE_FEATURE_FLAG_MAP are always available; gated ones
 * require the advisor to have the mapped feature flag enabled.
 */
export function isPolicyTypeEnabledForAdvisor(
  policyType: FundingPolicyType,
  featureFlags?: FeatureFlags,
  appConfigFlags?: AppConfigFeatureFlags,
): boolean {
  const flagKey = POLICY_TYPE_FEATURE_FLAG_MAP[policyType];
  if (flagKey && !featureFlags?.[flagKey]) {
    return false;
  }

  const appConfigKey = APP_CONFIG_POLICY_TYPE_FEATURE_FLAG_MAP[policyType];
  if (appConfigKey && !appConfigFlags?.[appConfigKey]) {
    return false;
  }

  // Ungated in both maps, or gated and switched on.
  return true;
}

export function calculateEnabledCarrierIds(featureFlags?: FeatureFlags): number[] {
  const enabledCarriers: number[] = [];

  for (const [carrierId, featureFlagKey] of Object.entries(CARRIER_FEATURE_FLAG_MAP)) {
    const carrierIdNum = Number(carrierId);

    if (featureFlagKey === null) {
      enabledCarriers.push(carrierIdNum);
    } else if (featureFlags?.[featureFlagKey]) {
      enabledCarriers.push(carrierIdNum);
    }
  }

  return enabledCarriers;
}
